<template>
  <div
    class="rs-textarea"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required">*</sup>
      <RsListingScoreHeart
        :show-heart="showHeart"
      />
    </label>
    <Field
      v-slot="{ errors }"
      class="veeValidate"
      :name="label ? label : name ? name : 'field'"
      :rules="fieldRules"
      :model-value="displayValue"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>This field is managed by <strong>[Data Source]</strong>.<br>Changing this will be temporary.</p>
      </div>
      <div class="input-group" role="group">
        <div
          v-if="imported"
          class="input-group-prepend"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon
              :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'"
            />
          </span>
        </div>
        <textarea
          :id="id"
          class="form-control"
          :class="{ 'is-invalid': !!errors[0] }"
          :disabled="disabled || (imported && isUnlocked == false)"
          :value="displayValue"
          :rows="rows"
          :placeholder="placeholder"
          :maxlength="maxlength"
          :style="{ resize: allowResize ? undefined : 'none' }"
          @input="changeInput"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
        />
      </div>
      <div
        v-if="errors.length || characterLimit !== null"
        class="rs-textarea__footer"
      >
        <div
          v-if="errors.length"
          class="text--error"
        >
          {{ errors[0] }}
        </div>
        <div
          v-if="characterLimit !== null"
          class="rs-textarea__counter"
          :class="{ 'rs-textarea__counter--exceeded': characterCount > characterLimit }"
        >
          {{ characterCount }}/{{ characterLimit }}
        </div>
      </div>
    </Field>
  </div>
</template>
<script lang="ts">
import { Field, defineRule } from 'vee-validate';
import { max } from '@vee-validate/rules';
import { defineComponent, PropType } from 'vue';
import { methods } from '@/mixins/validation-mixin';
import { decodeHtml, encodeHtml } from '@/shared';
import { embedCodeMsg, isEmbedCode } from '@/shared/helpers/is-embed-code';
import i18n from '@/i18n';

const multiEmailValidate = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return true;
  }

  const valueSet = (value ?? '').split('\n'); // value.replace(/\s/g, '');
  for (let i = 0; i < valueSet.length; i++) {
    if (valueSet[i] !== '' && !methods.isEmail(valueSet[i])) {
      return false;
    }
  }
  return true;
};
defineRule('multiEmail', (value?: string | string[]) => multiEmailValidate(value) || 'Emails are not valid.');

defineRule('multiEmailAlt', (value?: string | string[]) => multiEmailValidate(value) || 'Input is not valid email address(es).');

defineRule('embedCode', (value: string) => {
  const trimValue = (value ?? '').replace(/\s/g, '');

  if (trimValue === '') return true;
  return isEmbedCode(trimValue) || embedCodeMsg;
});

defineRule('validJson', (value: string) => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return true;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return 'Invalid JSON format.';
  }
});

defineRule('max', (value: unknown, params: [number]) => (max(value, params) ? true : i18n.t('validation.maxLength', { max: params[0] }) as string));

export default defineComponent({
  name: 'RsFormTextarea',
  components: {
    Field,
  },

  props: {
    rules: {
      type: Object,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    label: String,
    placeholder: String,
    alt: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [String, Number],
      default: '3',
    },
    maxRows: {
      type: [String, Number],
      default: '8',
    },
    maxlength: [String, Number],
    value: {
      type: [String, Number],
      default: '',
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: [String, Number] as PropType<string | number | null>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowResize: {
      type: Boolean,
      default: true,
    },
    imported: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'input',
    },
    showHeart: {
      type: Boolean,
      default: false,
    },

    htmlDecode: {
      type: Boolean,
      default: false,
    },
    /**
     * Color variants:  success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop:string) => [
        'success',
        'warning',
        'error',
        'error-alt',
      ].includes(prop),
    },
  },

  emits: ['blur', 'focus', 'input', 'update:modelValue'],
  data() {
    return {
      valid: null,
      isUnlocked: false,
      importHover: false,
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string | number {
      return (this.modelValue !== undefined && this.modelValue !== null) ? this.modelValue : this.value;
    },
    /**
     * The value rendered in the textarea, decoded when the consumer stores an
     * HTML-encoded string.
     *
     * This is also what the character counter measures AND what is handed to
     * vee-validate, so both report on the characters the user can actually see.
     * Validating `boundValue` instead would measure the encoded form, where a
     * single `&` costs five characters, and the counter and the error message
     * would disagree on any value containing entities.
     */
    displayValue(): string {
      const value = `${this.boundValue ?? ''}`;
      return this.htmlDecode ? decodeHtml(value) : value;
    },
    characterCount(): number {
      return this.displayValue.length;
    },
    /**
     * The character limit the counter reports against, read from the `max`
     * vee-validate rule so the limit has a single source of truth: the same
     * rule that produces the validation error also drives the counter.
     * `null` when no limit is applied, which hides the counter.
     */
    characterLimit(): number | null {
      const limit = Number(this.fieldRules.max);
      return Number.isFinite(limit) ? limit : null;
    },
    /**
     * The rule set handed to vee-validate. Normalizes the `rules` prop, whose
     * default resolves to `undefined`, into an object `Field` can always read.
     */
    fieldRules(): Record<string, unknown> {
      return this.rules ?? {};
    },
    inputClasses(): Record<string, boolean> {
      return {
        'rs-textarea--success': this.variant === 'success',
        'rs-textarea--warning': this.variant === 'warning',
        'rs-textarea--error': this.variant === 'error',
        'rs-textarea--error-alt': this.variant === 'error-alt',
        'rs-textarea--alt': this.alt,
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
  },
  methods: {
    decodeHtml,
    encodeHtml,

    changeInput(e: Event): void {
      const { value } = e.target as HTMLTextAreaElement;
      const out = this.htmlDecode ? encodeHtml(`${(value || '')}`) : value;
      // `update:modelValue` must fire before `input`: consumers that bind both v-model and
      // @input (e.g. RsDynamicSettingFormControl) read their own v-model state inside the
      // @input handler, and would otherwise see the value from before this keystroke. Vue 2
      // merged the v-model writer ahead of the consumer handler because both used `input`.
      this.$emit('update:modelValue', out);
      this.$emit('input', out);
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';
.rs-textarea {
  margin-bottom: 1rem;

  textarea {
    padding: $input-padding;
    width: 100%;
    background-color: $white;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    color: $input-color;
    font-size: $input-font-size;
    box-sizing: border-box;
    white-space: pre-line;
    &:focus {
      box-shadow: 0 0 0 3px rgba($info, 0.1);
      border-color: $info;
    }
    @include placeholder {
      color: rgba($dark-gray, 0.4);
    }
  }

  &__footer {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  &__counter {
    margin: 0.25rem 0.25rem 0.25rem auto;
    font-size: 0.75rem;
    white-space: nowrap;
    color: $dark-gray;

    &--exceeded {
      color: $error;
    }
  }

  &--error-alt {
    textarea {
      border-color: $error;
      color: $error;
      background-color: rgba($error, 0.3);
    }
  }
}
</style>
